import {
    AppBar, Avatar, Badge,
    Box,
    Divider, ListItemIcon, Menu, MenuItem,
    Toolbar, Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import React, {useMemo, useState} from "react";
import {
    Bell,
    LogOut,
    User
} from "lucide-react";
import {SideBar} from "./Drawer";
import {useAuth} from "../auth/AuthContext";
import {useLocation, useNavigate} from "react-router-dom";
import {environment} from "../../config/environment";

/**
 * Componente que renderiza o cabeçalho de uma página com título, subtítulo opcional
 * e conteúdo personalizado à direita.
 *
 * @param {object} props
 * @param {string} props.title - Título da página
 * @param {string} [props.subtitle] - Subtítulo da página
 * @param {Array<{icon: React.ReactNode, label: string, onClick: () => void}>} [props.itemsMenu] - Itens de menu para o drawer
 */
export default function PageHeader(
    {
        title,
        titleAction = () => null,
        subtitle,
        itemsMenu = [],
        onHandleDrawerToggle,
        onHandleMenuOpen,
        onToggleSubMenu,
        openParent,
        onProfileClick,
    }
) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const {logout} = useAuth();
    const [open, setOpen] = useState(openParent);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const handleDrawerToggle = () => {
        setOpen(!open);
        onHandleDrawerToggle?.(!open);
    };
    const handleMenuOpen = (id) => {
        onHandleMenuOpen?.(id);
    }
    const handleProfileMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const handleProfileClick = () => {
        handleMenuClose();
        if (onProfileClick) {
            onProfileClick();
            return;
        }
        navigate('/profile');
    };
    const handleLogout = () => {
        handleMenuClose();
        logout();
        navigate(environment.disableAuth ? '/' : '/login', {replace: true});
    };
    const toggleSubMenu = (id) => onToggleSubMenu?.(id);
    const drawerWidth = 260;
    const collapsedDrawerWidth = 72;
    const pathname = location?.pathname || '/';
    const isHomePath = pathname === '/';
    const menuItems = useMemo(() => {
        if (!Array.isArray(itemsMenu)) return [];
        if (isHomePath) return itemsMenu;

        const hasHomeItem = itemsMenu.some((item) => (
            item?.id === 'home'
            || item?.path === '/'
            || item?.to === '/'
            || item?.label?.toLowerCase() === 'home'
        ));
        if (hasHomeItem) return itemsMenu;

        return [{
            label: "Home",
            id: "home",
            selected: false,
            disabled: false,
            icon: <HomeOutlinedIcon />,
            onClick: () => navigate('/'),
        }, ...itemsMenu];
    }, [isHomePath, itemsMenu, navigate]);

    return (
        <Box sx={{ display: 'flex', minHeight: '100dvh' }}>

                {/* App Bar */}
                <AppBar
                    position="fixed"
                    color="default"
                    elevation={0}
                    sx={{
                        width: { md: `calc(100% - ${open ? drawerWidth : collapsedDrawerWidth}px)` },
                        ml: { md: `${open ? drawerWidth : collapsedDrawerWidth}px` },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        transition: theme.transitions.create(['margin', 'width'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                        zIndex: theme.zIndex.drawer - 1,
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { xs: 'block', md: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                        >
                            {title}
                        </Typography>

                        <Box sx={{ flexGrow: 1 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Notificações">
                                <IconButton color="inherit" size="large">
                                    <Badge badgeContent={4} color="error">
                                        <Bell size={20} />
                                    </Badge>
                                </IconButton>
                            </Tooltip>

                            <IconButton
                                size="large"
                                edge="end"
                                onClick={handleProfileMenuOpen}
                                color="inherit"
                                sx={{ ml: 1 }}
                            >
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: theme.palette.primary.main,
                                    }}
                                >
                                    A
                                </Avatar>
                            </IconButton>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Profile menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: { width: 200, mt: 1 }
                    }}
                >
                    <MenuItem onClick={handleProfileClick}>
                        <ListItemIcon>
                            <User size={18} />
                        </ListItemIcon>
                        <Typography variant="inherit">Meu perfil</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <Box
                                sx={{
                                    display: 'flex',
                                    color: 'error.main'
                                }}
                            >
                                <LogOut size={18} />
                            </Box>
                        </ListItemIcon>
                        <Typography variant="inherit" color="error">Sair</Typography>
                    </MenuItem>
                </Menu>
                {itemsMenu ? (
                    <SideBar
                        openParent={open}
                        itemsMenu={menuItems}
                        onHandleDrawerToggle={handleDrawerToggle}
                        onHandleMenuOpen={handleMenuOpen}
                        onToggleSubMenu={toggleSubMenu}
                        companyName="BusiFarm"
                    />
                    ) : null
                }
            </Box>
    );
}
